import React, { useState, useEffect } from 'react';
import { Package, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';
import barrelReplenishmentService from '../../services/barrelReplenishmentService';

import ReplenishmentRulesPanel from './components/ReplenishmentRulesPanel';
import ActiveTriggersPanel from './components/ActiveTriggersPanel';
import SupplierManagementPanel from './components/SupplierManagementPanel';
import PurchaseOrdersPanel from './components/PurchaseOrdersPanel';
import CreatePurchaseOrderModal from './components/CreatePurchaseOrderModal';
import SupplierPerformancePanel from './components/SupplierPerformancePanel';

const AutomatedReplenishmentControlCenter = () => {
  const [statistics, setStatistics] = useState(null);
  const [stockLevels, setStockLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePOModal, setShowCreatePOModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    loadData();
    const interval = setInterval(() => {
      loadData();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [refreshKey]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [stats, stock] = await Promise.all([
        barrelReplenishmentService?.getReplenishmentStatistics(),
        barrelReplenishmentService?.checkStockLevels()
      ]);
      setStatistics(stats);
      setStockLevels(stock);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const getBarrelTypeLabel = (type) => {
    return type === 'noir_270l' ? 'Noir 270L' : 'Bleu 220L';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Centre de Contrôle du Réapprovisionnement Automatisé
          </h1>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Actualiser
          </button>
        </div>
        <p className="text-gray-600">
          Gestion intelligente des stocks avec intégration fournisseurs et génération automatique de bons de commande
        </p>
      </div>

      {/* KPI Cards */}
      {!loading && statistics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-gray-500">Commandes Totales</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{statistics?.totalOrders || 0}</div>
            <div className="text-sm text-gray-600 mt-1">
              {statistics?.pendingOrders || 0} en attente
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-sm font-medium text-gray-500">Livrées</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{statistics?.deliveredOrders || 0}</div>
            <div className="text-sm text-gray-600 mt-1">
              {statistics?.inTransitOrders || 0} en transit
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
              </div>
              <span className="text-sm font-medium text-gray-500">Déclencheurs Actifs</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{statistics?.activeTriggers || 0}</div>
            <div className="text-sm text-gray-600 mt-1">
              {statistics?.completedTriggers || 0} complétés
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <span className="text-sm font-medium text-gray-500">Valeur Totale</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {statistics?.totalOrderValue?.toFixed(0) || 0}€
            </div>
            <div className="text-sm text-gray-600 mt-1">
              {statistics?.activeSuppliers || 0} fournisseurs actifs
            </div>
          </div>
        </div>
      )}

      {/* Stock Status Alert */}
      {stockLevels?.length > 0 && (
        <div className="mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              État des Stocks en Temps Réel
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {stockLevels?.map((stock, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border-2 ${
                    stock?.needs_replenishment
                      ? 'border-red-300 bg-red-50' :'border-green-300 bg-green-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">
                      {getBarrelTypeLabel(stock?.barrel_type)}
                    </span>
                    {stock?.needs_replenishment ? (
                      <span className="px-3 py-1 bg-red-600 text-white text-xs font-semibold rounded-full">
                        Réapprovisionnement Nécessaire
                      </span>
                    ) : (
                      <span className="px-3 py-1 bg-green-600 text-white text-xs font-semibold rounded-full">
                        Stock OK
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Stock actuel:</span>
                    <span className="font-bold text-gray-900">{stock?.current_stock} unités</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Seuil minimum:</span>
                    <span className="font-medium text-gray-700">{stock?.threshold} unités</span>
                  </div>
                  {stock?.needs_replenishment && (
                    <button
                      onClick={() => setShowCreatePOModal(true)}
                      className="mt-3 w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                    >
                      Créer Commande d'Urgence
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <ReplenishmentRulesPanel onRefresh={handleRefresh} />
        <ActiveTriggersPanel refreshKey={refreshKey} />
      </div>

      {/* Supplier Management */}
      <div className="mb-6">
        <SupplierManagementPanel onRefresh={handleRefresh} />
      </div>

      {/* Purchase Orders */}
      <div className="mb-6">
        <PurchaseOrdersPanel 
          refreshKey={refreshKey} 
          onCreateNew={() => setShowCreatePOModal(true)}
        />
      </div>

      {/* Supplier Performance */}
      <div className="mb-6">
        <SupplierPerformancePanel />
      </div>

      {/* Create Purchase Order Modal */}
      {showCreatePOModal && (
        <CreatePurchaseOrderModal
          onClose={() => setShowCreatePOModal(false)}
          onSuccess={() => {
            setShowCreatePOModal(false);
            handleRefresh();
          }}
        />
      )}
    </div>
  );
};

export default AutomatedReplenishmentControlCenter;